import React, { useEffect, useState } from "react";
import { Search, Download, ShieldAlert, Ban, CheckCircle2, RotateCcw, Trash2 } from "lucide-react";
import { api, apiError, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { useApp } from "@/context/AppContext";

const STATUS_COLORS = { approved: "bg-[#138808]/10 text-[#138808]", registered: "bg-slate-100 text-slate-600", blocked: "bg-red-100 text-red-700", disqualified: "bg-amber-100 text-amber-700" };

export default function Participants() {
  const { admin } = useApp();
  const [data, setData] = useState({ participants: [], total: 0, pages: 1 });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState(null);

  const load = async (p = page, s = search, st = status) => {
    try {
      const { data } = await api.get("/admin/participants", { params: { search: s, status: st === "all" ? "" : st, page: p, limit: 15 } });
      setData(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => { load(1); setPage(1); /* eslint-disable-next-line */ }, [status]);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/admin/participants/${id}`);
      setDetail(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const setUserStatus = async (id, st) => {
    try {
      await api.patch(`/admin/participants/${id}`, { status: st });
      toast.success(`Status set to ${st}`);
      load();
      if (detail?.id === id) openDetail(id);
    } catch (e) { toast.error(apiError(e)); }
  };

  const resetUser = async (id) => {
    if (!window.confirm("Reset ALL quiz attempts for this participant? This cannot be undone.")) return;
    try {
      await api.post(`/admin/participants/${id}/reset`);
      toast.success("Participant attempts reset");
      load();
      setDetail(null);
    } catch (e) { toast.error(apiError(e)); }
  };

  const purgeSeed = async () => {
    if (!window.confirm("Delete ALL seed/demo data (demo users, attempts, events)?")) return;
    try {
      const { data } = await api.post("/admin/purge-seed");
      toast.success(`Purged: ${JSON.stringify(data.deleted)}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="admin-participants">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-black font-heading text-slate-900 tracking-tighter">Participants</h1>
          <p className="text-sm text-slate-500 mt-1">{data.total} registrations</p>
        </div>
        <div className="flex gap-2">
          <a href={`${API}/admin/export/participants`} data-testid="export-participants" className="px-4 py-2.5 rounded-lg border border-slate-300 text-sm font-bold text-slate-600 hover:border-slate-500 transition-colors inline-flex items-center gap-2"><Download className="w-4 h-4" /> CSV</a>
          {admin?.role === "super_admin" && (
            <button onClick={purgeSeed} data-testid="purge-seed-btn" className="px-4 py-2.5 rounded-lg border border-red-300 text-sm font-bold text-red-600 hover:bg-red-50 transition-colors inline-flex items-center gap-2"><Trash2 className="w-4 h-4" /> Purge Demo Data</button>
          )}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="participant-search" className="pl-9" placeholder="Search name, email, display name, phone, Discord, Instagram…" value={search}
            onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(1)} />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="participant-status-filter" className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["all", "approved", "registered", "blocked", "disqualified"].map((s) => <SelectItem key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-5 bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-[10px] font-bold tracking-[0.15em] uppercase text-slate-400 border-b border-slate-200">
                <th className="text-left px-5 py-3">Player</th>
                <th className="text-left px-5 py-3">Contact</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-left px-5 py-3">Progress</th>
                <th className="text-left px-5 py-3">Flags</th>
                <th className="text-left px-5 py-3">Registered</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.participants.map((p) => (
                <tr key={p.id} onClick={() => openDetail(p.id)} className="hover:bg-slate-50 cursor-pointer transition-colors" data-testid={`participant-row-${p.display_name || p.id}`}>
                  <td className="px-5 py-3">
                    <div className="font-semibold text-slate-800">{p.display_name || "—"} {p.is_seed && <span className="text-[9px] font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded ml-1">DEMO</span>}</div>
                    <div className="text-xs text-slate-400">{p.name}</div>
                  </td>
                  <td className="px-5 py-3 text-xs text-slate-500">{p.email}<br />{p.phone}</td>
                  <td className="px-5 py-3"><span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${STATUS_COLORS[p.status] || "bg-slate-100 text-slate-500"}`}>{p.status}</span></td>
                  <td className="px-5 py-3 text-xs font-semibold text-slate-600">{p.categories_done} cats</td>
                  <td className="px-5 py-3">{p.events > 0 ? <span className="inline-flex items-center gap-1 text-xs font-bold text-red-600"><ShieldAlert className="w-3.5 h-3.5" />{p.events}</span> : <span className="text-xs text-slate-300">0</span>}</td>
                  <td className="px-5 py-3 text-xs text-slate-400">{p.registered_at ? new Date(p.registered_at).toLocaleDateString("en-IN") : "—"}</td>
                </tr>
              ))}
              {data.participants.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">No participants found.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between">
          <button disabled={page <= 1} onClick={() => { setPage(page - 1); load(page - 1); }} data-testid="participants-prev" className="text-sm font-bold text-slate-500 disabled:opacity-30">← Prev</button>
          <span className="text-xs text-slate-400 font-semibold">Page {page} / {data.pages}</span>
          <button disabled={page >= data.pages} onClick={() => { setPage(page + 1); load(page + 1); }} data-testid="participants-next" className="text-sm font-bold text-slate-500 disabled:opacity-30">Next →</button>
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="participant-detail-modal">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">{detail.display_name || "Participant"} <span className={`ml-2 text-[10px] font-bold uppercase px-2 py-1 rounded-full ${STATUS_COLORS[detail.status]}`}>{detail.status}</span></DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm mt-2">
                {[["Name", detail.name], ["Email", detail.email], ["Phone", detail.phone], ["Address", detail.address], ["Instagram", detail.instagram || "—"], ["Discord", detail.discord || "—"], ["Minecraft", detail.minecraft || "—"], ["Source", detail.referral_source || "—"], ["IP", detail.ip || "—"], ["Rank", detail.rank ? `#${detail.rank}` : "—"], ["Total Score", detail.total_score], ["Risk", `${detail.risk?.level?.toUpperCase()} (${detail.risk?.score})`]].map(([k, v]) => (
                  <div key={k}><div className="text-[10px] font-bold tracking-[0.15em] uppercase text-slate-400">{k}</div><div className="font-semibold text-slate-800 break-all">{v}</div></div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 mt-5">
                <button onClick={() => setUserStatus(detail.id, "approved")} data-testid="detail-approve" className="px-3 py-2 rounded-lg bg-[#138808] text-white text-xs font-bold inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Approve</button>
                <button onClick={() => setUserStatus(detail.id, detail.status === "blocked" ? "approved" : "blocked")} data-testid="detail-block" className="px-3 py-2 rounded-lg bg-red-600 text-white text-xs font-bold inline-flex items-center gap-1.5"><Ban className="w-3.5 h-3.5" /> {detail.status === "blocked" ? "Unblock" : "Block"}</button>
                <button onClick={() => setUserStatus(detail.id, "disqualified")} data-testid="detail-disqualify" className="px-3 py-2 rounded-lg bg-amber-500 text-white text-xs font-bold">Disqualify</button>
                <button onClick={() => resetUser(detail.id)} data-testid="detail-reset" className="px-3 py-2 rounded-lg border border-slate-300 text-slate-600 text-xs font-bold inline-flex items-center gap-1.5"><RotateCcw className="w-3.5 h-3.5" /> Reset Attempts</button>
              </div>
              <h3 className="font-heading font-bold text-slate-900 mt-6 mb-2">Category Attempts</h3>
              {detail.attempts?.length ? (
                <div className="space-y-1.5">
                  {detail.attempts.map((a) => (
                    <div key={a.id} className="flex justify-between text-sm bg-slate-50 rounded-lg px-4 py-2.5">
                      <span className="font-semibold text-slate-700">{a.category} <span className="text-xs text-slate-400">({a.status})</span></span>
                      <span className="font-timer font-bold text-[#FF9933]">{a.score} <span className="text-xs text-slate-400 font-sans">{a.correct}✓ {(a.time_ms / 1000).toFixed(0)}s</span></span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-slate-400">No attempts yet.</p>}
              <h3 className="font-heading font-bold text-slate-900 mt-6 mb-2">Anti-Cheat Timeline</h3>
              {detail.anticheat_events?.length ? (
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {detail.anticheat_events.map((e) => (
                    <div key={e.id} className="flex justify-between text-xs bg-red-50/50 border border-red-100 rounded-lg px-4 py-2">
                      <span className="font-semibold text-red-700">{e.type.replace(/_/g, " ")}</span>
                      <span className="text-slate-400">{new Date(e.ts).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-slate-400">No events. Clean record.</p>}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
